<script setup>
import { computed } from 'vue';
import CampaignFunnel from './CampaignFunnel.vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';
import VoiceCampaignDetails from './VoiceCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  // Whoever is only allowed to work the campaign, not to change it: the card opens, and that is all
  // it offers.
  readOnly: {
    type: Boolean,
    default: false,
  },
  isVoiceType: {
    type: Boolean,
    default: false,
  },
  phoneNumber: {
    type: String,
    default: '',
  },
  dialerConfig: {
    type: Object,
    default: () => ({}),
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  totalRecipientsCount: {
    type: Number,
    default: 0,
  },
  processedRecipientsCount: {
    type: Number,
    default: 0,
  },
  campaignSendsSummary: {
    type: Object,
    default: () => ({}),
  },
  engagement: {
    type: Object,
    default: () => ({}),
  },
  // Selection drives merging: the page collects the checked campaigns and merges them in one go.
  isSelectable: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'edit',
  'rename',
  'select',
  'delete',
  'stop',
  'viewSends',
  'open',
]);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';
const STATUS_CANCELLED = 'cancelled';

const { formatMessage } = useMessageFormatter();

const isStoppable = computed(
  () =>
    !props.isLiveChatType &&
    !props.isVoiceType &&
    [STATUS_PROCESSING, 'active'].includes(props.status)
);

const hasSends = computed(
  () =>
    !props.isLiveChatType &&
    !props.isVoiceType &&
    [STATUS_PROCESSING, STATUS_COMPLETED, STATUS_CANCELLED].includes(
      props.status
    )
);

const isProcessing = computed(() => props.status === STATUS_PROCESSING);

// Only a campaign that has finished sending can be merged - Campaigns::MergeService refuses the
// rest, because an active or in-flight campaign would lose the sends still ahead of it.
const showCheckbox = computed(
  () =>
    props.isSelectable &&
    !props.isLiveChatType &&
    !props.isVoiceType &&
    [STATUS_COMPLETED, STATUS_CANCELLED].includes(props.status)
);

const statusBadgeClass = computed(() => {
  if (props.isVoiceType) {
    return props.isEnabled
      ? 'bg-n-teal-3 text-n-teal-11'
      : 'bg-n-slate-3 text-n-slate-11';
  }

  if (props.isLiveChatType) {
    return props.isEnabled
      ? 'bg-n-teal-3 text-n-teal-11'
      : 'bg-n-slate-3 text-n-slate-11';
  }

  return (
    {
      [STATUS_COMPLETED]: 'bg-n-teal-3 text-n-teal-11',
      [STATUS_PROCESSING]: 'bg-n-amber-3 text-n-amber-11',
      [STATUS_CANCELLED]: 'bg-n-slate-3 text-n-slate-11',
    }[props.status] || 'bg-n-blue-3 text-n-blue-11'
  );
});

const campaignStatus = computed(() => {
  // A voice campaign is up or paused; it is never "scheduled", and nothing about it is sent.
  if (props.isVoiceType) {
    return props.isEnabled
      ? t('CAMPAIGN.VOICE.CARD.STATUS.ACTIVE')
      : t('CAMPAIGN.VOICE.CARD.STATUS.PAUSED');
  }

  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_PROCESSING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  }

  if (props.status === STATUS_CANCELLED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.CANCELLED');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const progressLabel = computed(
  () => `${props.processedRecipientsCount}/${props.totalRecipientsCount}`
);

const sentCount = computed(() => props.campaignSendsSummary.sent || 0);
const failedCount = computed(() => props.campaignSendsSummary.failed || 0);
const skippedCount = computed(() => props.campaignSendsSummary.skipped || 0);
const hasSendsSummary = computed(
  () =>
    hasSends.value &&
    sentCount.value + failedCount.value + skippedCount.value > 0
);

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});
</script>

<template>
  <CardLayout layout="row">
    <Checkbox
      v-if="showCheckbox"
      :model-value="isSelected"
      class="flex-shrink-0 self-start mt-1"
      @update:model-value="emit('select')"
    />
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md"
          :class="statusBadgeClass"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-if="!isVoiceType"
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <VoiceCampaignDetails
          v-if="isVoiceType"
          :phone-number="phoneNumber"
          :dialer-config="dialerConfig"
        />
        <LiveChatCampaignDetails
          v-else-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
      <div
        v-if="isProcessing && totalRecipientsCount > 0"
        class="flex items-center w-full gap-2"
      >
        <div class="w-full h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full rounded-full bg-n-amber-9"
            :style="{
              width: `${Math.min(100, (processedRecipientsCount / totalRecipientsCount) * 100)}%`,
            }"
          />
        </div>
        <span class="flex-shrink-0 text-xs text-n-slate-11 whitespace-nowrap">
          {{ progressLabel }}
        </span>
      </div>
      <div
        v-if="hasSendsSummary"
        class="flex items-center gap-3 text-xs text-n-slate-11"
      >
        <span>
          {{ t('CAMPAIGN.SMS.CARD.SENDS_SUMMARY.SENT', { count: sentCount }) }}
        </span>
        <span v-if="failedCount > 0" class="text-n-ruby-11">
          {{
            t('CAMPAIGN.SMS.CARD.SENDS_SUMMARY.FAILED', { count: failedCount })
          }}
        </span>
        <span v-if="skippedCount > 0">
          {{
            t('CAMPAIGN.SMS.CARD.SENDS_SUMMARY.SKIPPED', {
              count: skippedCount,
            })
          }}
        </span>
      </div>
      <CampaignFunnel
        v-if="!isVoiceType"
        :engagement="engagement"
        :total-recipients-count="totalRecipientsCount"
        class="w-full"
      />
    </div>
    <div class="flex items-center justify-end gap-2 flex-shrink-0">
      <Button
        v-if="isVoiceType"
        :label="t('CAMPAIGN.VOICE.CARD.OPEN')"
        variant="faded"
        size="sm"
        color="slate"
        @click="emit('open')"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        v-else-if="!readOnly"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-pencil"
        @click="emit('rename')"
      />
      <Button
        v-if="hasSends"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-list"
        :label="t('CAMPAIGN.SMS.CARD.VIEW_SENDS')"
        @click="emit('viewSends')"
      />
      <Button
        v-if="isStoppable"
        variant="faded"
        size="sm"
        color="amber"
        icon="i-lucide-circle-stop"
        @click="emit('stop')"
      />
      <Button
        v-if="!readOnly"
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
